import React from "react";
import type { CustomerConfig } from "../types";

type TestBookingState = {
  loading: boolean;
  status: "idle" | "success" | "error";
  message: string;
  responseBody?: string;
};

type Props = {
  selectedCustomer?: CustomerConfig | null;
  payloadIsValid: boolean;
  bookingPayloadPreview: string;
  testCreateBooking: () => void;
  testBookingState: TestBookingState;
  openOutputPreview: () => void;
};

export default function BookingTestPanel({
  selectedCustomer,
  payloadIsValid,
  bookingPayloadPreview,
  testCreateBooking,
  testBookingState,
  openOutputPreview,
}: Props) {
  const canTest =
    !testBookingState.loading &&
    payloadIsValid &&
    Boolean(selectedCustomer);

  const statusLabel =
    testBookingState.status === "success"
      ? "Success"
      : testBookingState.status === "error"
        ? "Failed"
        : "Idle";

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white/90 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/70 dark:shadow-2xl dark:shadow-black/20">
      <div className="border-b border-slate-200/80 px-5 py-4 dark:border-white/10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-base font-semibold tracking-tight text-slate-950 dark:text-white">
              Runtime test
            </div>

            <div className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
              Send a booking test to{" "}
              <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs dark:bg-slate-950">
                /api/fa-jdx-create-booking
              </code>
              .
            </div>
          </div>

          <button
            type="button"
            onClick={openOutputPreview}
            className="rounded-xl border border-slate-200 bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Expand
          </button>
        </div>
      </div>

      <div className="space-y-4 p-4">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-inner dark:border-white/10 dark:bg-slate-950">
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2 dark:border-white/10 dark:bg-slate-900">
            <div className="font-mono text-xs font-semibold text-slate-500 dark:text-slate-400">
              POST booking-request.json
            </div>

            <span className="rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-bold text-slate-600 dark:bg-white/10 dark:text-slate-300">
              Live preview
            </span>
          </div>

          <pre className="max-h-80 overflow-auto bg-white p-4 font-mono text-xs leading-5 text-slate-800 dark:bg-slate-950 dark:text-slate-100">
            {bookingPayloadPreview}
          </pre>
        </div>

        <button
          type="button"
          onClick={testCreateBooking}
          disabled={!canTest}
          className="w-full rounded-xl bg-orange-500 px-5 py-3 text-sm font-black text-white shadow-lg shadow-orange-500/10 transition hover:-translate-y-[1px] hover:bg-orange-400 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-slate-700 dark:disabled:text-slate-400"
        >
          {testBookingState.loading ? "Sending test..." : "Send test booking"}
        </button>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700 dark:border-white/10 dark:bg-slate-950/70 dark:text-slate-300">
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
              Execution result
            </div>

            <span
              className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${
                testBookingState.status === "success"
                  ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                  : testBookingState.status === "error"
                    ? "bg-rose-500/10 text-rose-700 dark:text-rose-300"
                    : "bg-slate-200 text-slate-600 dark:bg-white/10 dark:text-slate-400"
              }`}
            >
              {statusLabel}
            </span>
          </div>

          {testBookingState.status === "idle" && (
            <p>No test booking has been sent yet.</p>
          )}

          {testBookingState.status === "success" && (
            <p className="font-semibold text-emerald-600 dark:text-emerald-300">
              {testBookingState.message}
            </p>
          )}

          {testBookingState.status === "error" && (
            <p className="font-semibold text-rose-600 dark:text-rose-300">
              {testBookingState.message}
            </p>
          )}

          {testBookingState.responseBody && (
            <pre className="mt-3 max-h-52 overflow-auto rounded-xl border border-slate-200 bg-white p-3 font-mono text-xs text-slate-800 dark:border-white/10 dark:bg-slate-950 dark:text-slate-100">
              {testBookingState.responseBody}
            </pre>
          )}
        </div>
      </div>
    </section>
  );
}